import type {
  AdminVehicleCatalogAliasWriteDto,
  AdminVehicleCatalogCreateAliasBody,
  AdminVehicleCatalogCreateMakeBody,
  AdminVehicleCatalogCreateModelBody,
  AdminVehicleCatalogMakeDetailDto,
  AdminVehicleCatalogMakeListDto,
  AdminVehicleCatalogModelDto,
  AdminVehicleCatalogPatchMakeBody,
  AdminVehicleCatalogPatchModelBody,
  VehicleCatalogEntryStatus,
  VehicleCatalogSource,
} from '@carcheck/shared';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hooks + writes for «Справочник авто» (AUT-437 / VC-06),
 * against the AUT-436 `/admin/vehicle-catalog/*` catalogue API.
 *
 * Query keys: list → `['admin','vehicle-catalog','makes', params]`, detail →
 * `['admin','vehicle-catalog','make', id]`. Every write invalidates the
 * `['admin','vehicle-catalog']` prefix.
 *
 * `updatedAt` travels as the exact string the server sent (never parsed or
 * reformatted): the API compares it for its stale-write check.
 *
 * Only `PATCH /makes/:id` answers a full make, so it is the only write whose
 * result may replace the detail cache. Alias writes and model creation bump
 * the owner's `updatedAt` without returning it — the caller must refetch
 * the detail (see `refreshCatalog`) before the next PATCH.
 *
 * Writes pass `skipAutoErrorToast`: the page maps the error `code` itself
 * (`pages/vehicle-catalog/_lib/catalogErrors`).
 */
const BASE = '/admin/vehicle-catalog';

export const ADMIN_VEHICLE_CATALOG_KEY = ['admin', 'vehicle-catalog'] as const;

export const catalogMakeKey = (id: string): readonly unknown[] => [
  ...ADMIN_VEHICLE_CATALOG_KEY,
  'make',
  id,
];

export type CatalogStatusFilter = VehicleCatalogEntryStatus | 'all';

export interface CatalogMakesParams {
  status: CatalogStatusFilter;
  q?: string;
  onSale?: boolean;
  source?: VehicleCatalogSource;
  cursor: string | null;
  limit: number;
}

export const buildCatalogMakesParams = (p: CatalogMakesParams): Record<string, string> => {
  const query: Record<string, string> = { limit: String(p.limit) };
  if (p.status !== 'all') query.status = p.status;
  const q = p.q?.trim();
  if (q) query.q = q;
  if (p.onSale !== undefined) query.onSale = String(p.onSale);
  if (p.source) query.source = p.source;
  if (p.cursor) query.cursor = p.cursor;
  return query;
};

export const useCatalogMakes = (
  params: CatalogMakesParams,
): UseQueryResult<AdminVehicleCatalogMakeListDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_VEHICLE_CATALOG_KEY, 'makes', params],
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminVehicleCatalogMakeListDto>(`${BASE}/makes`, {
        params: buildCatalogMakesParams(params),
        signal,
      });
      return data;
    },
  });

/** Off while no make is open. */
export const useCatalogMake = (
  id: string | null,
): UseQueryResult<AdminVehicleCatalogMakeDetailDto, Error> =>
  useQuery({
    queryKey: catalogMakeKey(id ?? ''),
    enabled: id !== null,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminVehicleCatalogMakeDetailDto>(
        `${BASE}/makes/${id ?? ''}`,
        { signal },
      );
      return data;
    },
  });

const quiet = { skipAutoErrorToast: true } as const;

export const createCatalogMake = async (
  body: AdminVehicleCatalogCreateMakeBody,
): Promise<AdminVehicleCatalogMakeDetailDto> =>
  (await apiClient.post<AdminVehicleCatalogMakeDetailDto>(`${BASE}/makes`, body, quiet)).data;

export const patchCatalogMake = async (
  id: string,
  body: AdminVehicleCatalogPatchMakeBody,
): Promise<AdminVehicleCatalogMakeDetailDto> =>
  (await apiClient.patch<AdminVehicleCatalogMakeDetailDto>(`${BASE}/makes/${id}`, body, quiet))
    .data;

export const createCatalogModel = async (
  makeId: string,
  body: AdminVehicleCatalogCreateModelBody,
): Promise<AdminVehicleCatalogModelDto> =>
  (await apiClient.post<AdminVehicleCatalogModelDto>(`${BASE}/makes/${makeId}/models`, body, quiet))
    .data;

export const patchCatalogModel = async (
  id: string,
  body: AdminVehicleCatalogPatchModelBody,
): Promise<AdminVehicleCatalogModelDto> =>
  (await apiClient.patch<AdminVehicleCatalogModelDto>(`${BASE}/models/${id}`, body, quiet)).data;

export const addCatalogAlias = async (
  body: AdminVehicleCatalogCreateAliasBody,
): Promise<AdminVehicleCatalogAliasWriteDto> =>
  (await apiClient.post<AdminVehicleCatalogAliasWriteDto>(`${BASE}/aliases`, body, quiet)).data;

export const deleteCatalogAlias = async (id: string): Promise<AdminVehicleCatalogAliasWriteDto> =>
  (await apiClient.delete<AdminVehicleCatalogAliasWriteDto>(`${BASE}/aliases/${id}`, quiet)).data;

export interface CatalogCache {
  /** The full make a `PATCH /makes/:id` answered — the one write allowed to replace the detail. */
  putMake: (make: AdminVehicleCatalogMakeDetailDto) => void;
  /** Swap one model inside its make's cached detail (`PATCH /models/:id` answer). */
  spliceModel: (makeId: string, model: AdminVehicleCatalogModelDto) => void;
  /** Invalidate the whole catalogue prefix; resolves once active queries refetched. */
  refreshCatalog: () => Promise<void>;
}

export const useCatalogCache = (): CatalogCache => {
  const queryClient = useQueryClient();
  return {
    putMake: (make) => queryClient.setQueryData(catalogMakeKey(make.id), make),
    spliceModel: (makeId, model) =>
      queryClient.setQueryData<AdminVehicleCatalogMakeDetailDto>(catalogMakeKey(makeId), (prev) =>
        prev ? { ...prev, models: prev.models.map((m) => (m.id === model.id ? model : m)) } : prev,
      ),
    refreshCatalog: () => queryClient.invalidateQueries({ queryKey: ADMIN_VEHICLE_CATALOG_KEY }),
  };
};
